import { PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, Select, Space, Table, message } from "antd";
import type { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { PageTitle, SSNToggle } from "../../components";
import { ranksApi, type RatingUser } from "../../_helpers/apiService";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";

interface DataType {
  key: string;
  id: number;
  lastName: string;
  firstName: string;
  email: string;
  phoneNumber: string;
  ssn: string;
  rankId?: number;
  rankName?: string;
  rankOrder?: number;
  rateDate?: string;
  rate2?: number;
  rateDate2?: string;
  teamId?: number;
  teamName?: string;
  totalChansaa: number;
}

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};

const tailLayout = {
  wrapperCol: { offset: 6, span: 18 },
};

const deleteArcher = async (user: any) => {
  // await deleteDoc(doc(db, "users", user.key));
  // message.success(
  //   `"${user.lastName} ${user.firstName}" харваач амжилттай устгалаа.`
  // );
};

const { Option } = Select;

function Ranking() {
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [data, setData] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchKey, setSearchKey] = useState("firstName");
  const [searchValue, setSearchValue] = useState<any>(null);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const userData = useSelector((state: any) => state.authentication.userData);

  useEffect(() => {
    fetchArchers(1, 50);
  }, []);

  const fetchArchers = async (page: number = 1, pageSize: number = 50, search?: { key: string, value: string }) => {
    setLoading(true);
    try {
      // Calculate skip based on page and pageSize
      const skip = (page - 1) * pageSize;

      // Build query params with search
      const params: any = {
        club_id: CURRENT_CLUB_ID,
        limit: pageSize,
        skip: skip,
      };

      // Add search parameter if provided
      if (search && search.value) {
        params[search.key] = search.value;
      }

      // Fetch rating data with chansaa calculations from backend
      const response = await ranksApi.getRating(params);

      // Map to expected format
      const archers: DataType[] = response.data.items.map((user: RatingUser) => ({
        key: user.user_id.toString(),
        id: user.user_id,
        lastName: user.last_name,
        firstName: user.first_name,
        email: user.email,
        phoneNumber: user.phone_number,
        ssn: user.ssn,
        rankId: user.rank_id,
        rankName: user.rank_name,
        rankOrder: user.rank_order,
        rateDate: user.rank_date,
        rate2: user.rate_id,
        rateDate2: user.rate_date,
        teamId: user.team_id,
        teamName: user.team_name,
        totalChansaa: user.total_chansaa,
      }));

      setData(archers);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: response.data.total,
      });
    } catch (error: any) {
      console.error("Fetch archers error:", error);
      message.error("Харваачдын мэдээлэл татахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = () => {
    // Reset to first page when searching
    if (searchValue) {
      // Map frontend search keys to backend API keys
      const keyMap: any = {
        firstName: 'first_name',
        lastName: 'last_name',
        email: 'email',
        rankName: 'rank_name',
      };

      const apiKey = keyMap[searchKey] || searchKey;
      fetchArchers(1, pagination.pageSize, { key: apiKey, value: searchValue });
    } else {
      fetchArchers(1, pagination.pageSize);
    }
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const clearSearchForm = () => {
    searchForm.resetFields();
    setSearchValue(null);
    fetchArchers(1, pagination.pageSize);
  };

  const handleTableChange = (newPagination: any, filters: any, sorter: any) => {
    // Maintain search when changing pages
    if (searchValue) {
      const keyMap: any = {
        firstName: 'first_name',
        lastName: 'last_name',
        email: 'email',
        rankName: 'rank_name',
      };

      const apiKey = keyMap[searchKey] || searchKey;
      fetchArchers(newPagination.current, newPagination.pageSize, { key: apiKey, value: searchValue });
    } else {
      fetchArchers(newPagination.current, newPagination.pageSize);
    }
  };
  const getRanking = () => {};
  const columns: ColumnsType<DataType> = [
    {
      title: "№",
      dataIndex: "id",
      key: "id",
      width: 80,
      fixed: 'left',
      render: (text, row, index) => <>{index + 1}</>,
    },
    {
      title: "Чансаа",
      dataIndex: "totalChansaa",
      key: "totalChansaa",
      width: 100,
    },
    {
      title: "Овог",
      dataIndex: "lastName",
      key: "lastName",
      width: 120,
    },
    {
      title: "Нэр",
      dataIndex: "firstName",
      key: "firstName",
      width: 120,
    },
    {
      title: "Регистрийн дугаар",
      dataIndex: "ssn",
      key: "ssn",
      width: 140,
      render: (ssn, record) => <SSNToggle userId={record.id} />,
    },
    {
      title: "Улсын цол",
      dataIndex: "rankName",
      key: "rankName",
      width: 150,
      render: (value) => value || <span>-</span>,
    },
    {
      title: "Улсын цол авсан огноо",
      dataIndex: "rateDate",
      key: "rateDate",
      width: 150,
      render: (value) => {
        if (!value || !dayjs(value).isValid()) {
          return "-";
        }
        return dayjs(value).format("YYYY-MM-DD");
      },
    },
    {
      title: "Спортын зэрэг",
      dataIndex: "rate2",
      key: "rate2",
      width: 150,
      render: (value) => value || <span>-</span>,
    },
    {
      title: "Спортын зэрэг авсан огноо",
      dataIndex: "rateDate2",
      key: "rateDate2",
      width: 180,
      render: (value) => {
        if (!value || !dayjs(value).isValid()) {
          return "-";
        }
        return dayjs(value).format("YYYY-MM-DD");
      },
    },
    {
      title: "Баг",
      dataIndex: "teamName",
      key: "teamName",
      width: 150,
      render: (value) => value || <span>-</span>,
    },
    {
      title: "Имэйл хаяг",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Утасны дугаар",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: 130,
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Link to={`/archers/${record.key}`} key={record.key}>
            Засах
          </Link>
          {/* <Popconfirm
            title="Устгахдаа итгэлтэй байна уу?"
            onConfirm={() => deleteArcher(record)}
            okText="Тийм"
            cancelText="Үгүй"
          >
            <Button type="link">Устгах</Button>
          </Popconfirm> */}
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        title="Харваачид"
        description="Харваачдын чансаа"
        right={
          userData.role === "admin" ? (
            <> </>
          ) : (
            <>
              <Button
                shape="round"
                onClick={() => setVisible(true)}
                icon={<PlusOutlined />}
                size="large"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Харваач нэмэх
              </Button>
              {/* <Button
                shape="round"
                onClick={() => checkJson()}
                icon={<PlusOutlined />}
                size="large"
              >
                Импорт {oldData.length}
              </Button> */}
            </>
          )
        }
      />
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-12">
                      <Form
                        layout="inline"
                        style={{ marginBottom: 10 }}
                        form={searchForm}
                        initialValues={{ type: searchKey }}
                      >
                        <Form.Item name="type" label="Талбар">
                          <Select
                            value={searchKey}
                            style={{ width: 300 }}
                            onChange={(val) => {
                              setSearchKey(val);
                              setSearchValue(null);
                            }}
                            allowClear
                          >
                            <Option value="lastName">Овогоор</Option>
                            <Option value="firstName">Нэрээр</Option>
                            <Option value="email">Имэйл хаягаар</Option>
                            <Option value="rankName">Цолоор</Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="name"
                          label="Хайх утга"
                          rules={[
                            {
                              required: true,
                              message: "Хайх утга оруулна уу!",
                            },
                          ]}
                        >
                          <Input
                            onChange={(e: any) =>
                              setSearchValue(e.target.value)
                            }
                            defaultValue={searchValue}
                            value={searchValue}
                          />
                        </Form.Item>
                        <Form.Item {...tailLayout}>
                          <Button
                            type="primary"
                            onClick={() => onFinish()}
                            loading={loading}
                          >
                            Хайх
                          </Button>
                        </Form.Item>
                        <Form.Item {...tailLayout}>
                          <Button
                            type="dashed"
                            onClick={() => clearSearchForm()}
                          >
                            Арилгах
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={data}
                      loading={loading}
                      scroll={{ x: 'max-content' }}
                      pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `Нийт ${total} харваач`,
                        pageSizeOptions: ['10', '20', '50', '100'],
                      }}
                      onChange={handleTableChange}
                    />
                  </div>

                  {/* <Pagination
                    current={currentPage}
                    totalPages={totalPages}
                    prevAction={prevPage}
                    nextAction={nextPage}
                    changeAction={(num) => changePage(num)}
                  /> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default Ranking;
